/** Detalle de una corrida: tablas extraídas y resultado de cada validación. */

import { useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import { ErrorState, Loading } from "../components/States";
import { StatusBadge } from "../components/StatusBadge";
import { runLabel, runTone, severityTone } from "../components/tones";
import { formatCompact, formatDateTime } from "../components/format";

export function RunDetail() {
  const { runId = "" } = useParams();
  const { data, error, loading } = useAsync(() => api.run(runId), [runId]);

  if (loading) return <Loading what="la corrida" />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const validations = data.validations ?? [];
  const tables = data.tables ?? [];
  const blocking = validations.filter((v) => !v.passed && v.severity === "blocking");

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Corrida</h1>
          <p className="mono">{data.sync_run_id}</p>
        </div>
        <StatusBadge tone={runTone(data.status)}>{runLabel(data.status)}</StatusBadge>
      </div>

      {data.error && (
        <div className="card banner critical">
          <StatusBadge tone="critical">Error</StatusBadge>
          <div className="banner-body">
            <strong>La corrida no se publicó</strong>
            <p className="small mono">{data.error}</p>
            <p className="small muted">
              El snapshot sigue en RAW, aislado: no se borra, y las vistas actuales
              siguen apuntando a la última corrida publicada.
            </p>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Procedencia</h2>
        <dl className="facts">
          <dt>Ambiente</dt>
          <dd>{data.environment}</dd>
          <dt>Base de origen</dt>
          <dd className="mono">{data.source_database ?? "—"}</dd>
          <dt>Inicio</dt>
          <dd>{formatDateTime(data.started_at)}</dd>
          <dt>Fin</dt>
          <dd>{formatDateTime(data.finished_at)}</dd>
          <dt>Mapping</dt>
          <dd className="mono">{data.mapping_version ?? "—"}</dd>
          <dt>Manifiesto</dt>
          <dd>v{data.manifest_version ?? "—"}</dd>
          <dt>Disparada por</dt>
          <dd>{data.triggered_by ?? "—"}</dd>
        </dl>
      </div>

      <div className="card">
        <h2>
          Validaciones{" "}
          {blocking.length > 0 && (
            <StatusBadge tone="critical">{blocking.length} bloqueantes</StatusBadge>
          )}
        </h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Resultado</th>
                <th scope="col">Regla</th>
                <th scope="col">Severidad</th>
                <th scope="col">Observado</th>
                <th scope="col">Esperado</th>
              </tr>
            </thead>
            <tbody>
              {validations.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    La corrida no llegó a evaluar validaciones.
                  </td>
                </tr>
              )}
              {validations.map((validation) => (
                <tr key={validation.rule}>
                  <td>
                    <StatusBadge tone={validation.passed ? "good" : severityTone(validation.severity)}>
                      {validation.passed ? "pasa" : "falla"}
                    </StatusBadge>
                  </td>
                  <td>
                    <div className="mono">{validation.rule}</div>
                    <div className="small muted">{validation.detail}</div>
                  </td>
                  <td>{validation.severity}</td>
                  <td className="small mono">{validation.observed ?? "—"}</td>
                  <td className="small mono">{validation.expected ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Tablas extraídas</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Tabla</th>
                <th scope="col" className="num">
                  Filas
                </th>
                <th scope="col" className="num">
                  Segundos
                </th>
                <th scope="col">Hash de contenido</th>
                <th scope="col">Observaciones</th>
              </tr>
            </thead>
            <tbody>
              {tables.map((table) => (
                <tr key={table.raw_table}>
                  <td className="mono">{table.raw_table}</td>
                  <td className="num">{formatCompact(table.row_count)}</td>
                  <td className="num">{table.duration_seconds.toFixed(1)}</td>
                  <td className="mono small">
                    {table.content_hash ? table.content_hash.slice(0, 16) : "—"}
                  </td>
                  <td className="small">
                    {table.skipped && (
                      <StatusBadge tone="warning">omitida: {table.skip_reason}</StatusBadge>
                    )}
                    {(table.missing_columns ?? []).length > 0 && (
                      <span className="muted">
                        {table.missing_columns!.length} columnas ausentes, cargadas como NULL
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
